import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import moment from 'moment';
import { isReadEmpty, isSameRead, toStoredRead, toUsageFromReads } from './toSaveRows';

/*
 * A single-reading template maps no start columns: each row of a file holds one reading, which is the end read, and the
 * start of the row comes from an earlier cycle, a second file of readings (the start reads), or what the manager types.
 * Everything here is plain data in, plain data out; the screen keeps the state.
 */

const hasText = value => typeof value === 'string' && value.trim() !== '';
const MAPPING_FIELDS = ['startReadDateColumn', 'startReadColumn', 'usageColumn', 'endReadDateColumn', 'endReadColumn', 'meterIdColumn', 'unitNameColumn', 'uomColumn'];

/**
 * What a template is held against: its id, mapped columns and unit mappings. Edited in place it keeps its id and changes its
 * columns or unit mappings, so the id alone does not say whether openings read under it still mean the same thing.
 */
export const toTemplateKey = template => {
  var _template$unitMapping;
  const unitMappings = ((_template$unitMapping = template.unitMappings) !== null && _template$unitMapping !== void 0 ? _template$unitMapping : []).map(_ref => {
    let unitId = _ref.unitId,
      identifiers = _ref.identifiers,
      isCommonArea = _ref.isCommonArea;
    return {
      unitId,
      identifiers: [...(identifiers !== null && identifiers !== void 0 ? identifiers : [])].sort(),
      isCommonArea: !!isCommonArea
    };
  }).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  return "".concat(template.id, "|").concat(MAPPING_FIELDS.map(field => {
    var _template$field;
    return String((_template$field = template[field]) !== null && _template$field !== void 0 ? _template$field : '').trim();
  }).join('\u0001'), "|").concat(JSON.stringify(unitMappings));
};

/**
 * Whether a parse response shows a single reading file on its own: a start from an earlier cycle, or a row with no start.
 * An interval file has a start on every row and no earlier cycle. Used only while the template cannot be told.
 */
export const looksSingleReading = response => {
  var _response$rows;
  return !!(response !== null && response !== void 0 && response.baselineBillingCycleId) || ((_response$rows = response === null || response === void 0 ? void 0 : response.rows) !== null && _response$rows !== void 0 ? _response$rows : []).some(row => isReadEmpty(row.startMeterRead));
};

/** Whether a saved template maps neither start column. A template that cannot be told is not single-reading. */
export const isSingleReadingTemplate = template => !!template && !hasText(template.startReadColumn) && !hasText(template.startReadDateColumn);
const NOT_LAST_MONTH_CODE = 'opening_read_not_last_month';
// What the backend says about the start of a row. Each is worked out again from the grid after every merge or edit, so
// the ones a response carries are not shown as they came.
const OPENING_WARNING_CODES = ['opening_read_needed', 'opening_read_no_date', NOT_LAST_MONTH_CODE, 'start_after_end'];
const DUPLICATE_METER_CODES = ['duplicate_meter_row', 'duplicate_unit_without_meter_id'];

/** A response's own warnings: the file's, without the ones about where a start came from. */
export const withoutOpeningWarnings = warnings => (warnings !== null && warnings !== void 0 ? warnings : []).filter(w => !OPENING_WARNING_CODES.includes(w.code));

/** Whether a file has two rows for one meter, a row dropped as invalid included. Such a file is not used. */
export const hasDuplicateMeters = warnings => (warnings !== null && warnings !== void 0 ? warnings : []).some(w => DUPLICATE_METER_CODES.includes(w.code));
export const findNotLastMonthWarning = response => {
  var _find, _response$warnings;
  return (_find = ((_response$warnings = response === null || response === void 0 ? void 0 : response.warnings) !== null && _response$warnings !== void 0 ? _response$warnings : []).find(w => w.code === NOT_LAST_MONTH_CODE)) !== null && _find !== void 0 ? _find : null;
};

// The backend's MeterKey upper cases with the invariant culture one character at a time, so a character that would
// expand (the sharp s becomes SS in JavaScript) stays as it is. Lower casing differs on a final sigma, so it is not used.
const upperInvariant = character => {
  const upper = character.toUpperCase();
  return [...upper].length === 1 ? upper : character;
};

/** A meter id as the backend matches it: trimmed, invariant upper case, leading zeroes kept. */
const normalizeMeterId = value => [...String(value !== null && value !== void 0 ? value : '').trim()].map(upperInvariant).join('');

/** What an opening is kept under: the unit and the normalized meter id. */
export const toOpeningKey = row => "".concat(String(row.unitId || row.unitName || '').toLowerCase(), "::").concat(normalizeMeterId(row.meterSerialNumber));

/** The opening a row of a Start reads file gives: that row's own end read and end date. */
export const toUploadedOpening = row => {
  var _row$endMeterDateOnly, _row$unitsOfMeasure, _row$unitName, _row$meterSerialNumbe;
  return {
    // Held at the four decimals the backend stores, like every read that enters the grid.
    startMeterRead: toStoredRead(row.endMeterRead),
    startMeterDateOnly: (_row$endMeterDateOnly = row.endMeterDateOnly) !== null && _row$endMeterDateOnly !== void 0 ? _row$endMeterDateOnly : '',
    unitsOfMeasure: (_row$unitsOfMeasure = row.unitsOfMeasure) !== null && _row$unitsOfMeasure !== void 0 ? _row$unitsOfMeasure : '',
    source: 'uploaded',
    unitName: (_row$unitName = row.unitName) !== null && _row$unitName !== void 0 ? _row$unitName : '',
    meterSerialNumber: (_row$meterSerialNumbe = row.meterSerialNumber) !== null && _row$meterSerialNumbe !== void 0 ? _row$meterSerialNumbe : ''
  };
};

/**
 * The opening a manager typed into the Start read or Start date cell of a row. A cleared read is an opening too: an
 * explicit empty one, which an end file uploaded again does not fill from the earlier cycle. A date edited on a start that
 * is still the same read keeps where that read came from and its unit of measure.
 */
export const toTypedOpening = (row, previous) => {
  var _row$startMeterDateOn, _row$unitName2, _row$meterSerialNumbe2;
  const startMeterRead = isReadEmpty(row.startMeterRead) ? '' : row.startMeterRead;
  const startMeterDateOnly = (_row$startMeterDateOn = row.startMeterDateOnly) !== null && _row$startMeterDateOn !== void 0 ? _row$startMeterDateOn : '';
  if (previous && startMeterRead !== '' && isSameRead(previous.startMeterRead, startMeterRead)) {
    return _objectSpread(_objectSpread({}, previous), {}, {
      startMeterDateOnly
    });
  }
  return {
    startMeterRead,
    startMeterDateOnly,
    unitsOfMeasure: '',
    source: 'typed',
    unitName: (_row$unitName2 = row.unitName) !== null && _row$unitName2 !== void 0 ? _row$unitName2 : '',
    meterSerialNumber: (_row$meterSerialNumbe2 = row.meterSerialNumber) !== null && _row$meterSerialNumbe2 !== void 0 ? _row$meterSerialNumbe2 : ''
  };
};

/** A row as the end file gave it: a start it came with is from the earlier cycle. */
export const toEndRow = (row, _ref2) => {
  let toInitialRate = _ref2.toInitialRate;
  // The reads are held at the four decimals the backend stores, and the usage is the one between those reads, so the
  // grid shows the Usage Save sends.
  const startMeterRead = toStoredRead(row.startMeterRead);
  const endMeterRead = toStoredRead(row.endMeterRead);
  return _objectSpread(_objectSpread({}, row), {}, {
    startMeterRead,
    endMeterRead,
    meterRate: toInitialRate(row.meterRate),
    meterUsage: toUsageFromReads(startMeterRead, endMeterRead),
    openingSource: isReadEmpty(startMeterRead) ? null : 'previous',
    openingUnitsOfMeasure: ''
  });
};

/** The rows with each opening laid over the row it matches, and no other row touched. Usage follows the reads. */
export const applyOpenings = (rows, openings) => rows.map(row => {
  const opening = openings.get(toOpeningKey(row));
  if (!opening) return row;
  return _objectSpread(_objectSpread({}, row), {}, {
    startMeterRead: opening.startMeterRead,
    startMeterDateOnly: opening.startMeterDateOnly,
    meterUsage: toUsageFromReads(opening.startMeterRead, row.endMeterRead),
    openingSource: isReadEmpty(opening.startMeterRead) ? null : opening.source,
    openingUnitsOfMeasure: opening.unitsOfMeasure
  });
});

/** The starts no row matches yet: held, shown, and never a charge. A cleared start has no read to show. */
export const findPendingOpenings = (rows, openings) => {
  const keys = new Set(rows.map(toOpeningKey));
  return [...openings].filter(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      key = _ref4[0],
      opening = _ref4[1];
    return !keys.has(key) && !isReadEmpty(opening.startMeterRead);
  }).map(_ref5 => {
    let _ref6 = _slicedToArray(_ref5, 2),
      opening = _ref6[1];
    return opening;
  });
};
const isDate = value => moment(value, moment.ISO_8601, true).isValid();

/** A start date later than the end date. A date that is missing or not a date says nothing. */
export const isStartAfterEnd = row => isDate(row.startMeterDateOnly) && isDate(row.endMeterDateOnly) && moment(row.startMeterDateOnly).isAfter(moment(row.endMeterDateOnly), 'day');
const sameUnit = (a, b) => String(a !== null && a !== void 0 ? a : '').trim().toLowerCase() === String(b !== null && b !== void 0 ? b : '').trim().toLowerCase();

/** A start file in one unit of measure and the end file in another. Nothing is converted. */
export const hasUnitConflict = row => hasText(row.openingUnitsOfMeasure) && hasText(row.unitsOfMeasure) ? !sameUnit(row.openingUnitsOfMeasure, row.unitsOfMeasure) : false;
export const findStartAfterEndRows = rows => rows.filter(isStartAfterEnd);
export const findUnitConflictRows = rows => rows.filter(hasUnitConflict);
const meterLabel = row => row.meterSerialNumber || '(none)';

/**
 * What the grid says about the start of each row, worked out from the grid as it is now. The row index is 0: these
 * come from rows, not from lines of a file, so they carry no file row number.
 */
export const toOpeningWarnings = (rows, _ref7) => {
  let notLastMonthWarning = _ref7.notLastMonthWarning;
  const warnings = [];
  rows.forEach(row => {
    const where = "unit ".concat(row.unitName, ", meter ").concat(meterLabel(row));
    if (isReadEmpty(row.startMeterRead)) {
      warnings.push({
        rowIndex: 0,
        code: 'opening_read_needed',
        message: "Opening read needed for ".concat(where, ": enter it or upload a start reads file.")
      });
      return;
    }
    if (!hasText(row.startMeterDateOnly)) {
      warnings.push({
        rowIndex: 0,
        code: 'opening_read_no_date',
        message: "The start read for ".concat(where, " has no date.")
      });
    }
    if (isStartAfterEnd(row)) {
      warnings.push({
        rowIndex: 0,
        code: 'start_after_end',
        message: "The start read date for ".concat(where, " is after its end read date.")
      });
    }
    if (hasUnitConflict(row)) {
      warnings.push({
        rowIndex: 0,
        code: 'start_unit_conflict',
        message: "The start reads for ".concat(where, " are in ").concat(row.openingUnitsOfMeasure.trim(), ", the end reads in ").concat(String(row.unitsOfMeasure).trim(), ".")
      });
    }
  });
  // The earlier cycle is only worth a notice while a row still takes its start from it.
  if (notLastMonthWarning && rows.some(row => row.openingSource === 'previous')) warnings.push(notLastMonthWarning);
  return warnings;
};
const MONTH_FORMAT = 'MMMM YYYY';
const monthOf = summary => moment({
  year: summary.year,
  month: summary.month - 1,
  day: 1
});
const findSummaryOfCycle = (summaries, propertyId, billingCycleId) => summaries.find(summary => {
  var _summary$billingCycle;
  return summary.propertyId === propertyId && [...((_summary$billingCycle = summary.billingCycleSummaries) !== null && _summary$billingCycle !== void 0 ? _summary$billingCycle : [])].some(cycle => cycle.billingCycleId === billingCycleId);
});

/**
 * The month the starts taken from the earlier cycle are from, or '' when it cannot be told without asking the
 * server. In order: the month of the cycle the response names, when the billing summaries already loaded list it;
 * the month the not last month warning names; the month before this cycle, which is where the backend looks first
 * and says nothing when it finds the starts there.
 */
export const toBaselineMonth = _ref8 => {
  var _notLastMonth$message;
  let billingSummary = _ref8.billingSummary,
    propertyId = _ref8.propertyId,
    baselineBillingCycleId = _ref8.baselineBillingCycleId,
    billingCycleId = _ref8.billingCycleId,
    notLastMonth = _ref8.notLastMonth;
  const summaries = [...(billingSummary !== null && billingSummary !== void 0 ? billingSummary : [])];
  const baseline = baselineBillingCycleId && findSummaryOfCycle(summaries, propertyId, baselineBillingCycleId);
  if (baseline) return monthOf(baseline).format(MONTH_FORMAT);
  const named = /from (.+?), not last month/i.exec((_notLastMonth$message = notLastMonth === null || notLastMonth === void 0 ? void 0 : notLastMonth.message) !== null && _notLastMonth$message !== void 0 ? _notLastMonth$message : '');
  if (named) return named[1];
  if (notLastMonth) return '';
  const current = findSummaryOfCycle(summaries, propertyId, billingCycleId);
  return current ? monthOf(current).subtract(1, 'month').format(MONTH_FORMAT) : '';
};

/** The words in the Start source column. */
export const toSourceLabel = (source, baselineMonth) => {
  if (source === 'uploaded') return 'Uploaded';
  if (source === 'typed') return 'Typed';
  if (source === 'previous') return baselineMonth ? "From ".concat(baselineMonth) : 'From the previous cycle';
  return '';
};